import { useEffect, useState } from "react";
import { FileDown, Printer } from "lucide-react";

/**
 * FormDownload
 * -------------
 * A small "Download printable form" card that renders a button linking to a
 * static PDF/DOCX under /forms/. Auto-hides itself if the file isn't present
 * on the server (so pages don't show broken download links).
 *
 * Usage:
 *   <FormDownload
 *     label="TIOB Event Creation Procedure"
 *     file="/forms/event-creation.pdf"
 *     description="Fillable PDF - download, complete, and email to the office."
 *   />
 *
 * Drop new form files into /app/frontend/public/forms/ and add a FormDownload
 * card wherever you want it. No admin work needed until we build a full forms
 * manager.
 */
export function FormDownload({ label, file, description, className = "" }) {
  const [exists, setExists] = useState(null); // null = probing, true/false = result

  useEffect(() => {
    let alive = true;
    fetch(file, { method: "HEAD" })
      .then((r) => {
        if (!alive) return;
        // The SPA fallback returns 200 with text/html for unknown paths.
        // Treat as "exists" only when the content-type is NOT text/html.
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        setExists(r.ok && !ct.startsWith("text/"));
      })
      .catch(() => alive && setExists(false));
    return () => {
      alive = false;
    };
  }, [file]);

  if (exists === false) return null; // hide gracefully when the PDF is missing

  const ext = (file.split(".").pop() || "").toLowerCase();
  const kind = ext === "docx" || ext === "doc" ? "DOCX" : "PDF";

  return (
    <aside
      className={`rounded-2xl bg-[color:var(--cream)] p-5 md:p-6 flex flex-wrap items-center gap-4 justify-between ${className}`}
      data-testid={`form-download-${file.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "")}`}
    >
      <div className="flex items-start gap-3 min-w-0">
        <Printer className="w-6 h-6 text-[color:var(--blue-deep)] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-semibold text-[color:var(--blue-ink)]">{label}</p>
          {description && (
            <p className="text-sm text-[color:var(--ink-soft)] mt-1">{description}</p>
          )}
          <p className="text-xs text-[color:var(--muted)] mt-1 uppercase tracking-wider font-mono-accent">
            {kind} · printable
          </p>
        </div>
      </div>
      <a
        href={file}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-ghost shrink-0"
      >
        <FileDown className="w-4 h-4" /> Download
      </a>
    </aside>
  );
}

export default FormDownload;
